import { createWithEqualityFn } from 'zustand/traditional';
import { MajorListSort, MajorSearchFilters, SortDirection } from '@common-types';
import { DESKTOP_PER_PAGE_OPTIONS } from '@constants';
import { MajorListSortOption } from '@constants/stateSpotlight';
import type { SortByValues } from '@pages/Scholarships/components/SortBy';
import { ResultFilters } from '@store/types/filters';

export interface MajorSearchState extends MajorSearchFilters, ResultFilters {
  [key: string]: string[] | boolean | number | undefined;
}

export type MajorSearchParams = Partial<MajorSearchFilters> & Pick<MajorSearchState, 'skip' | 'limit'>;

export type MajorSortByValues = SortByValues<MajorListSortOption>;

export const INITIAL_MAJOR_SORT: MajorSortByValues = {
  sort: MajorListSort.CIP_TITLE,
  sortDirection: SortDirection.ASC,
};

export interface MajorSearchStore {
  filters: MajorSearchState;
  sortByValues: MajorSortByValues;
  isSortSelected: boolean;
  setSortByValues: (sortByValues: MajorSortByValues) => void;
  setDefaultSortByValues: (sortByValues: MajorSortByValues) => void;
  setSearchFilters: (searchFilters: Partial<MajorSearchState>) => void;
  removeSearchFilter: (searchFilter: keyof MajorSearchState) => void;
  removeSearchFilterValue: (searchFilter: { key: keyof MajorSearchFilters; value: unknown }) => void;
  resetSearchFilters: (searchFilters?: Partial<MajorSearchState>) => void;
}

const initialState = {
  filters: {
    limit: DESKTOP_PER_PAGE_OPTIONS[0],
  } as MajorSearchState,
  sortByValues: INITIAL_MAJOR_SORT,
  isSortSelected: false,
};

export const useMajorSearchStore = createWithEqualityFn<MajorSearchStore>()(
  (set, get) => ({
    ...initialState,

    setSortByValues: (sortByValues) => {
      set({ sortByValues, isSortSelected: true });
    },

    setDefaultSortByValues: (sortByValues) => {
      set({ sortByValues });
    },

    setSearchFilters: (searchFilters) => {
      set({
        filters: {
          ...get().filters,
          ...searchFilters,
        },
      });
    },

    removeSearchFilter: (searchFilter) => {
      const filters = { ...get().filters };
      delete filters[searchFilter];
      set({
        filters,
      });
    },

    removeSearchFilterValue: (searchFilter: { key: keyof MajorSearchFilters; value: unknown }) => {
      const filters = { ...get().filters };

      if (typeof searchFilter.value === 'boolean') {
        delete filters[searchFilter.key];
        set({
          filters: {
            limit: filters.limit, // Keep only limit when removing boolean filter
          },
        });
        return;
      }

      const currentFilter = filters[searchFilter.key] as string[];
      const newValue = currentFilter.filter((item) => item !== searchFilter.value);

      if (newValue.length === 0) {
        delete filters[searchFilter.key];
      } else {
        filters[searchFilter.key as string] = newValue;
      }

      set({
        filters,
      });
    },

    resetSearchFilters: (searchFilters: MajorSearchState = {}) => {
      set({
        filters: searchFilters,
      });
    },
  }),
  Object.is,
);
